/**
 * 香川県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は福岡・北海道・山形と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「骨付鳥発祥の地・丸亀」と断定しない。丸亀市公式は「発祥」という語を使っていない。
 *  - 「うどん県」は正式名称ではない。県が愛称として使っているだけ。
 *  - ハマチ養殖は「世界初」ではなく「日本初」。
 *  - 伊吹いりこ・讃岐和三盆糖はGI登録産品ではない。
 *  - しょうゆ豆の由来は諸説。断定しない。
 *  - 鯛めしは農水省の香川県一覧に無いので採用しない（鯛そうめんが正式に収載）。
 *  - うどんに「〇〇市発祥」「〇〇店が元祖」と書かない。
 *
 * 本番画像は public/assets/foods/kagawa/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：オリーブの実・和三盆の干菓子・いりこ・鯛そうめん（店で食べられる品に差し替え）
 * - 醤油ソフトクリーム：「小豆島のご当地ソフトクリームの元祖」は県観光協会・小豆島観光協会のページにあるが、特定の店の品を指す言い方で、店の宣伝になるため書かない
 * - 醤油ソフトクリーム：特定の醤油蔵・売店の名前は書かない（店の宣伝にならないように）
 * - 小豆島手延べそうめん：伝来の年（慶長3年など）は検索結果の要約に出たが、開いた公式ページでは確かめられなかったので書かない（「約400年前」のみ書いた）
 * - オリーブ牛：給与期間（「出荷前2か月以上」など）は検索結果の要約に出たが、開いた県・振興会のページでは確かめられなかったので書かない
 * - オリーブ牛：始めた人の個人名は書かない（振興会ページは「小豆島の肥育農家」とのみ書く）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { KAGAWA_FOOD_HULLS } from './kagawaFoodShapes.generated';
import { kagawaSceneryTour } from './kagawaScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'shoyu-soft-cream',
    level: 1,
    name: '醤油ソフトクリーム',
    shortName: '醤油ソフト',
    image: '/assets/foods/kagawa/01_shoyu_soft_cream.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '小豆島の醤油を加えたソフトクリーム。醤（ひしお）の郷ならではの一品で、醤油の香ばしさがキャラメルのような味わいになる。ワッフルのコーンにのせて出す店もある。',
    source:
      'https://www.my-kagawa.jp/gourmet/feature/gourmet/soft',
    icon: { label: '醤油ソフト', shape: 'berry', baseColor: '#d9b48a', accentColor: '#c98a3c' },
    reward: {
      unlockScore: 200,
      area: '小豆島',
      headline: '醤油の香ばしさがキャラメル味に',
      tip: '天然醸造の蔵で木桶に仕込んだ「生しょうゆ」を使ったものもある。小豆島には珍しいご当地ソフトクリームが数多くある。',
      links: searchLinks('小豆島', '醤油ソフトクリーム'),
      official: [{ label: '小豆島観光協会「醤油ソフトクリーム」', url: 'https://shodoshima.or.jp/sightseeing/detail.php?id=19' }],
    },
  },
  {
    id: 'teppai',
    level: 2,
    name: 'てっぱい',
    shortName: 'てっぱい',
    image: '/assets/foods/kagawa/02_teppai.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '魚を三枚におろして細く切り、塩と酢でしめてから、短冊の大根や細ねぎと讃岐の白味噌の酢味噌であえる。もとはため池のフナで作り、いまはコノシロやサゴシ、サバで代用する。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/teppai_kagawa.html',
    icon: { label: 'てっぱい', shape: 'sashimi-platter', baseColor: '#efe3b8', accentColor: '#5a9e3a' },
    reward: {
      unlockScore: 500,
      area: '香川県内全域（讃岐平野）',
      headline: 'ため池のフナを酢味噌であえる',
      tip: 'ため池の水を抜く冬に作られた。昔は伊勢講や寄り合いで出され、冬の客膳に欠かせなかった。名は「鉄砲和え」がなまったとされる。',
      links: searchLinks('香川県', 'てっぱい'),
    },
  },
  {
    id: 'shouyu-mame',
    level: 3,
    name: 'しょうゆ豆',
    shortName: 'しょうゆ豆',
    image: '/assets/foods/kagawa/03_shouyu_mame.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '乾燥そら豆を、甘辛く。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/shouyu_mame_kagawa.html',
    icon: { label: 'しょうゆ豆', shape: 'berry', baseColor: '#6b4a2a', accentColor: '#c89a52' },
    reward: {
      unlockScore: 1000,
      area: '香川県',
      headline: '乾燥そら豆を醤油・砂糖・唐辛子で味つけする。江戸時代に讃岐で生まれたとされる。',
      tip: '常備菜として一年中食べられ、おせちで黒豆の代わりに使う家庭もある。由来には小豆島の醤油醸造が起こりという説や、お遍路さんへの煎りそら豆が醤油の壺に落ちたという伝承がある。',
      links: searchLinks('香川県', 'しょうゆ豆'),
    },
  },
  {
    id: 'bukkake-udon',
    level: 4,
    name: 'ぶっかけうどん',
    shortName: 'ぶっかけ',
    image: '/assets/foods/kagawa/04_bukkake_udon.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '冷たい麺に、濃いつゆを直接。',
    source: 'https://www.my-kagawa.jp/udon/feature/sanukiudon/mainmenu',
    icon: { label: 'ぶっかけ', shape: 'noodle-bowl', baseColor: '#f0e6cf', accentColor: '#5c4a32' },
    reward: {
      unlockScore: 1800,
      area: '香川県',
      headline: '冷たい麺に濃いめのつゆを直接かけ、ねぎや大根おろしと豪快に混ぜて食べる。',
      tip: '讃岐うどんのだしは、いりこ（煮干し）やかつおが主体。',
      links: searchLinks('香川県', 'ぶっかけうどん'),
    },
  },
  {
    id: 'kamatama-udon',
    level: 5,
    name: '釜玉うどん',
    shortName: '釜玉',
    image: '/assets/foods/kagawa/05_kamatama_udon.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '釜あげの熱で、卵を半熟にする。',
    source: 'https://www.my-kagawa.jp/udon/feature/sanukiudon/mainmenu',
    icon: { label: '釜玉', shape: 'noodle-bowl', baseColor: '#f0d88c', accentColor: '#c9a45a' },
    reward: {
      unlockScore: 3000,
      area: '香川県',
      headline: '釜あげの熱い麺に、生卵と醤油をからめる。',
      tip: '熱い麺が卵をちょうどよく包み、濃厚な香りとなめらかな味わいになる。',
      links: searchLinks('香川県', '釜玉うどん'),
    },
  },
  {
    id: 'kake-udon',
    level: 6,
    name: 'かけうどん',
    shortName: 'かけ',
    image: '/assets/foods/kagawa/06_kake_udon.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: 'だしと、ねぎと、天かす。',
    source: 'https://www.my-kagawa.jp/udon/feature/sanukiudon/mainmenu',
    icon: { label: 'かけ', shape: 'noodle-bowl', baseColor: '#e8e0cc', accentColor: '#7f9a48' },
    reward: {
      unlockScore: 4500,
      area: '香川県',
      headline: 'いちばん基本のうどん。いりこやかつおのだしに、ねぎと天かすというシンプルな薬味。',
      tip: '香川県は「そば・うどん店事業所数」「うどん用小麦粉使用量」「うどん・そばの年間支出金額」がいずれも全国1位。',
      links: searchLinks('香川県', 'かけうどん'),
    },
  },
  {
    id: 'shippoku-udon',
    level: 7,
    name: 'しっぽくうどん',
    shortName: 'しっぽく',
    image: '/assets/foods/kagawa/07_shippoku_udon.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '冬の野菜を、煮干しだしで煮込む。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/sippoku_udon_kagawa.html',
    icon: { label: 'しっぽく', shape: 'pot', baseColor: '#8a9a7c', accentColor: '#f0e8d4' },
    reward: {
      unlockScore: 6500,
      area: '香川県',
      headline: '秋から冬の野菜（大根・人参・豆腐・油揚げ・ねぎ）を煮干しだしで煮込み、ゆでたうどんにかける。',
      tip: '冬に来客をもてなすときや、年末に年越しそばの代わりとして食べられることもある。',
      links: searchLinks('香川県', 'しっぽくうどん'),
    },
  },
  {
    id: 'shodoshima-somen',
    level: 8,
    name: '小豆島手延べそうめん',
    shortName: '小豆島そうめん',
    image: '/assets/foods/kagawa/08_shodoshima_somen.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '小豆島特産のごま油を塗りながら練った生地を、木箸を使って極細の糸状になるまで引き延ばし、天日でゆっくり乾かす。約400年前、池田村の人が奈良の三輪から技を学んだのが始まりとされる。',
    source:
      'https://www.my-kagawa.jp/shodoshima/feature/shodoshima/beauty4',
    icon: { label: 'そうめん', shape: 'noodle-bowl', baseColor: '#f6f3ea', accentColor: '#4f8a3a' },
    reward: {
      unlockScore: 9000,
      area: '小豆島町',
      headline: 'ごま油を塗って細く細く延ばす',
      tip: '冬の農閑期に家族の労働だけで作れることから島に根づいた。産地では出来立ての「生そうめん」も味わえ、夏は冷たく、冬は温かくして食べる。',
      links: searchLinks('小豆島', '手延べそうめん'),
      official: [{ label: '小豆島町「小豆島手延素麺PR動画『島の光』」', url: 'https://www.town.shodoshima.lg.jp/kanko/other_info/specialty/shima_no_hikari.html' }, { label: '小豆島手延素麺協同組合', url: 'https://www.shimanohikari.or.jp/' }],
    },
  },
  {
    id: 'anmochi-zoni',
    level: 9,
    name: 'あんもち雑煮',
    shortName: 'あんもち雑煮',
    image: '/assets/foods/kagawa/09_anmochi_zoni.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '白味噌の汁に、あん入りの丸餅。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/anmochi_zouni_kagawa.html',
    icon: { label: '雑煮', shape: 'pot', baseColor: '#f0e6cf', accentColor: '#c9a45a' },
    reward: {
      unlockScore: 12000,
      area: '香川県',
      headline: 'あん入りの丸餅を白味噌仕立ての汁に入れ、大根・金時人参・豆腐を合わせる。',
      tip: '江戸時代のさとうきび奨励で讃岐白砂糖が特産になり、貴重な砂糖を年に一度の正月に使う特別な料理として、明治のころに定着した。',
      links: searchLinks('香川県', 'あんもち雑煮'),
    },
  },
  {
    id: 'honetsukidori',
    level: 10,
    name: '骨付鳥',
    shortName: '骨付鳥',
    image: '/assets/foods/kagawa/10_honetsukidori.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '骨付きのもも肉を、釜で焼き上げる。',
    source: 'https://www.city.marugame.lg.jp/page/3054.html',
    icon: { label: '骨付鳥', shape: 'curry-plate', baseColor: '#7a4a24', accentColor: '#e8c060' },
    reward: {
      unlockScore: 16000,
      area: '丸亀',
      headline: '鶏の骨付きもも肉をオーブン釜などで焼き上げる、全国的にも珍しい料理。1950年代に丸亀市の飲食店で生まれたとされる。',
      tip: '噛みごたえのある「おやどり」と、柔らかい「ひなどり」の2種。キャベツやおにぎりを添えて食べ、県内ではクリスマスの定番でもある。',
      links: searchLinks('丸亀市', '骨付鳥'),
    },
  },
  {
    id: 'olive-hamachi',
    level: 11,
    name: 'オリーブハマチ',
    shortName: 'オリーブハマチ',
    image: '/assets/foods/kagawa/11_olive_hamachi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '県花と県魚が出会って生まれた。',
    source: 'https://www.pref.kagawa.lg.jp/suisan/sogo/oshirase/2025orihamacp.html',
    icon: { label: 'ハマチ', shape: 'sashimi-platter', baseColor: '#e8a08a', accentColor: '#7f9a48' },
    reward: {
      unlockScore: 22000,
      area: '香川県',
      headline: '香川県産を主とした「オリーブ葉」の粉末を添加した餌を、規定の回数与えて育てる。平成19年に誕生した。',
      tip: 'オレウロペインの抗酸化作用で酸化・変色しにくく、さっぱりした味になる。昭和3年に野網和三郎が安戸池でハマチの餌付けに成功したのが日本初のハマチ養殖で、平成7年11月7日にハマチは県の魚になった。',
      links: searchLinks('香川県', 'オリーブハマチ'),
    },
  },
  {
    id: 'olive-gyu',
    level: 12,
    name: 'オリーブ牛',
    shortName: 'オリーブ牛',
    image: '/assets/foods/kagawa/12_olive_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '県産オリーブの採油後の果実を乾かした「オリーブ飼料」を一定期間与えて育てた黒毛和牛。2011年に讃岐牛の新ブランドとして登場した。柔らかい肉質と、後味のさっぱりした脂身が特長。',
    source:
      'https://www.pref.kagawa.lg.jp/documents/17216/r5_9shunnoichioshi.pdf',
    icon: { label: 'ステーキ', shape: 'feast', baseColor: '#b5523b', accentColor: '#5b3a24' },
    reward: {
      unlockScore: 30000,
      area: '香川県',
      headline: 'オリーブの搾り果実で育てた和牛',
      tip: '小豆島の肥育農家が、オリーブオイルを搾ったあとの果実を飼料にすることに成功したのが始まり。2017年の全国和牛能力共進会で特別賞「脂肪の質賞」を受けた。',
      links: searchLinks('香川県', 'オリーブ牛'),
      official: [{ label: '香川県「オリーブ畜産物 概要」', url: 'https://www.pref.kagawa.lg.jp/chikusan/olive/gaiyou/gaiyou.html' }, { label: '讃岐牛・オリーブ牛振興会「オリーブ牛のはじまり」', url: 'https://olivefedwagyu.jp/about/' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const kagawaFoodStage: StageDefinition = {
  regionId: 'kagawa',
  regionName: '香川県',
  mode: 'food',
  title: '香川めしマージ',
  theme: {
    primaryColor: '#4f7a4a',
    secondaryColor: '#d9a441',
    accentColor: '#f2d16b',
    backgroundColor: '#161a16',
  },
  /** 背景ツアー「香川めぐり」。軸は季節。5月から翌4月へ一周する。 */
  scenery: {
    tour: kagawaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, KAGAWA_FOOD_HULLS),
};
